import type { ImageData } from './imageHelpers'

const STAGED_NEW_BOARD_KEY = 'printboard_staged_new_board_v1'

export interface StagedNewBoardPayload {
  images: ImageData[]
  inspiration?: {
    mood: string
    prompt: string
  }
}

type StagedNewBoardMap = Record<string, StagedNewBoardPayload>

const canUseSessionStorage = (): boolean =>
  typeof window !== 'undefined' && !!window.sessionStorage

const readStagedBoards = (): StagedNewBoardMap => {
  if (!canUseSessionStorage()) return {}

  const raw = window.sessionStorage.getItem(STAGED_NEW_BOARD_KEY)
  if (!raw) return {}

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return parsed as StagedNewBoardMap
  } catch {
    return {}
  }
}

const writeStagedBoards = (value: StagedNewBoardMap): void => {
  if (!canUseSessionStorage()) return
  window.sessionStorage.setItem(STAGED_NEW_BOARD_KEY, JSON.stringify(value))
}

export const stageNewBoardPayload = (
  boardId: string,
  payload: StagedNewBoardPayload
): void => {
  const next = readStagedBoards()
  next[boardId] = payload
  writeStagedBoards(next)
}

export const getStagedNewBoardPayload = (
  boardId: string
): StagedNewBoardPayload | null => {
  const staged = readStagedBoards()[boardId]
  return staged ?? null
}

export const clearStagedNewBoardPayload = (boardId: string): void => {
  const next = readStagedBoards()
  if (!(boardId in next)) return
  delete next[boardId]
  writeStagedBoards(next)
}
